/**
 * WCC360 — Career roadmap + pathway routes (presentation only).
 *
 * Renders the derived stages from `src/lib/careerRoadmap.ts`. Fully keyboard
 * operable (native <details>/<summary>), no colour-only meaning, and no
 * motion beyond what `prefers-reduced-motion` allows.
 */
import { ChevronDown, Coins, Compass, Clock, Route } from "lucide-react";
import type { CareerIntel } from "@/lib/careerIntel";
import { careerPathwayRoutes, careerRoadmap, type RoadmapStage } from "@/lib/careerRoadmap";
import { useLearnerStart } from "@/hooks/useLearnerStart";

const TIER_LABEL: Record<number, string> = {
  1: "Shortest route",
  2: "Middle route",
  3: "Longer route",
  4: "Longest route",
};

function TierDots({ tier }: { tier: number }) {
  return (
    <span className="inline-flex items-center gap-1" aria-hidden>
      {[1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-1.5 rounded-full ${i <= tier ? "bg-primary" : "bg-border"}`}
        />
      ))}
    </span>
  );
}

export function CareerPathways({ intel }: { intel: CareerIntel }) {
  const routes = careerPathwayRoutes(intel);
  if (!routes.length) return null;

  return (
    <ol className="space-y-3">
      {routes.map((r, i) => (
        <li
          key={`${r.kind}-${r.label}`}
          className="rounded-3xl border border-border bg-card p-5 md:p-6"
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary">
              <Route className="h-3.5 w-3.5" aria-hidden />
              {r.kind}
            </span>
            {i === 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300">
                <Coins className="h-3.5 w-3.5" aria-hidden />
                Cheapest workable route
              </span>
            )}
            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <TierDots tier={r.tier} />
              {TIER_LABEL[r.tier]}
            </span>
          </div>
          <p className="mt-3 text-[15px] leading-relaxed text-foreground/90">{r.label}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" aria-hidden />
              {r.duration}
            </span>
            <span>{r.note}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function CareerRoadmapTimeline({ intel }: { intel: CareerIntel }) {
  // Drawn from where this learner actually stands, so a senior role shows the
  // route to it rather than telling a beginner to apply for it today.
  const start = useLearnerStart();
  const stages = careerRoadmap(intel, start);
  return <RoadmapTimeline stages={stages} />;
}

export function RoadmapTimeline({ stages }: { stages: RoadmapStage[] }) {
  if (!stages.length) return null;

  return (
    <ol className="relative space-y-3 md:pl-6">
      <span
        aria-hidden
        className="hidden md:block absolute left-[9px] top-3 bottom-3 w-px bg-border"
      />
      {stages.map((s, i) => (
        <li key={s.id} className="relative">
          <span
            aria-hidden
            className="hidden md:block absolute -left-6 top-6 h-[19px] w-[19px] rounded-full border-2 border-primary bg-background"
          />
          <details
            className="group rounded-3xl border border-border bg-card open:border-primary/40"
            open={i === 0}
          >
            <summary className="flex cursor-pointer items-start gap-3 p-5 md:p-6 rounded-3xl list-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background">
              <span className="min-w-0 flex-1">
                <span className="block text-xs uppercase tracking-[0.16em] text-primary">
                  Step {i + 1} · {s.horizon}
                </span>
                <span className="mt-1 block font-display text-lg md:text-xl font-medium">
                  {s.title}
                </span>
                <span className="mt-1 block text-sm text-muted-foreground leading-relaxed">
                  {s.summary}
                </span>
              </span>
              <ChevronDown
                className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none group-open:rotate-180"
                aria-hidden
              />
            </summary>
            <div className="border-t border-border px-5 md:px-6 py-4">
              <ul className="space-y-2.5">
                {s.actions.map((a) => (
                  <li key={a} className="flex gap-3 text-[15px] leading-relaxed text-foreground/90">
                    <Compass className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        </li>
      ))}
    </ol>
  );
}
